import { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EditNotesModal } from "../Table/components/TableModals/EditNotesModal/EditNotesModal";
import { useDispatchedActions, useTranslation } from "../../../../../hooks";
import { rowPerPageOptions } from "../../../../../utils/constants";
import { clearObjectEmptyValues } from "../../../../../utils/helpers/clearObjectEmptyValues";
import { ApiService } from "../../../../../services/ApiService";
import { errorToast } from "../../../../../utils/helpers/customToast.helper";
import { IconSvg } from "../../../../../utils/constants/icons/iconSvg.constant";
import { CheckBox } from "../../../../../components/ui/CheckBoxes/CheckBox";
import { FormatCellFilter } from "../Table/components/TableColumns/FormatCellFilter";
import { FormatCellNotesHead } from "../Table/components/TableColumns/FormatCellNotesHead";
import { FormatCellCheckbox } from "../Table/components/TableColumns";
import { FormatCellNotesData } from "../Table/components/TableColumns/FormatCellNotesData";
import { Button } from "../../../../../components/ui/Buttons/Button/Button";
import { Loading } from "../../../../../components/ui/Loading/Loading";
import { NoDataComponent } from "../../../../../components/common/NoDataComponent/NoDataComponent";
import { Pagination } from "../../../../../components/common/Pagination/Pagination";
import { getBorderColor } from "../../../../../utils/helpers/getBorderColor";
import { UpdateTableDataModal } from "../Table/components/TableModals/UpdateTableDataModal";
import { CustomModal } from "../../../../../components/ui/CustomModal/CustomModal";
import "../Table/Table.scss";
import { getCountryFlag } from "../../../../../utils/helpers/getCountryFlag";
import { getCountryName } from "../../../../../utils/helpers/getCountryName";
import { moveTo } from "../../../../../utils/helpers/moveTo";
import { formatDate } from "../../../../../utils/helpers/formatDate";
import { getDaysToDeadline } from "../../../../../utils/helpers/getDaysToDeadline";
import { scrollToElementById } from "../../../../../utils/helpers/scrollToElementById";
import { TableRowLoader } from "../Table/components/ui/TableRowLoader/TableRowLoader";
import { fetchingDelayTime } from "../../../../../utils/constants/fetchingDelayTime";

export const RenewalTable = ({ controller, setRenewalProxyCount }) => {
  const { lang } = useParams();
  const navigate = useNavigate();
  // Dispatch
  const {
    setSelectedRows,
    setTableRowLoading
  } = useDispatchedActions();

  // **Redux State
  const { cabinet: { orders, orders: { formBtn, column: { proxyExpire } } }, main: { buyBtn } } = useTranslation();
  const { select: { noOptionsMessage }, cabinet: { balance } } = useTranslation();
  const {
    selectedRows,
    selectedFolder,
    isTableRowLoading
  } = useSelector((state) => state.cabinet);
  const { activeData, countries } = useSelector((state) => state.content);

  // **Local state
  const [localSize, setLocalSize] = useState(10);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [tableData, setTableData] = useState({});
  const [isTableLoaded, setIsTableLoaded] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [searchParams] = useSearchParams();

  const dateStart = searchParams.get("dateStart") || null;
  const dateEnd = searchParams.get("dateEnd") || null;

  const { skin } = useSelector((state) => state.header);

  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      ref.current.select.setValue({ label: `${localSize} ${balance?.lines}`, value: localSize });
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const fetchTableData = async ({ page, size, proxyType, filterParams, sort, controller, filterByDateParams }) => {
    const loadingInterval = setTimeout(() => setIsTableLoaded(false), fetchingDelayTime);

    try {
      clearObjectEmptyValues(filterParams);
      clearObjectEmptyValues(sort);

      const params = { ...filterParams, proxyType };

      const { data, status } = await ApiService.getAllRenewalUserTableData(page, size, params, sort, controller, filterByDateParams);

      if (status !== 200) {
        throw new Error();
      }

      setRenewalProxyCount(data?.totalElements);
      setTableData(data);
    } catch (err) {
      errorToast("Something went wrong");
    } finally {
      clearTimeout(loadingInterval);
      setIsTableLoaded(true);
    }
  };

  const filterParams = {
    country: searchParams.get("country"),
    runningOut: true,
    description: searchParams.get("description")
  };

  const filterByDateParams = {
    filterOffDate: !!dateStart || !!dateEnd,
    filterOffDateStart: dateStart,
    filterOffDateEnd: dateEnd
  };

  const sort = {
    [searchParams.get("sort")]: searchParams.get("order")
  };

  const isPaginationVisible = tableData?.totalElements > 10;
  const TOP_OF_TABLE = 420;

  // Page change handler
  const handlePageChange = (page) => {
    if (page === localCurrentPage) return;

    moveTo(0, TOP_OF_TABLE);
    setLocalCurrentPage(page);
    fetchTableData({
      page: page - 1,
      size: localSize,
      proxyType: activeData?.proxy?.code,
      selectedFolder,
      filterParams,
      sort,
      filterByDateParams
    });
  };

  // Rows size handler
  const handlePerRowsChange = (newPerPage) => {
    if (newPerPage?.value === localSize) return;

    setLocalSize(newPerPage?.value);
    setLocalCurrentPage(1);
    fetchTableData({
      page: 0,
      size: Number(newPerPage?.value),
      proxyType: activeData?.proxy?.code,
      selectedFolder,
      filterParams,
      sort,
      filterByDateParams
    });
  };
  // Define rows per page options
  const defineRowsPerPageOptions = () =>
    [
      ...rowPerPageOptions.filter((item) => item < tableData?.totalElements),
      tableData?.totalElements
    ].map((item) => ({ label: `${item} ${balance?.lines}`, value: item }));

  const getValueByCountryCode = (code) => {
    if (!tableData?.countryIds) return [];

    const item = tableData.countryIds.find((obj) => obj.hasOwnProperty(code));
    return item ? item[code] : null;
  };

  const selectAllIpsByCountry = (e, country) => {
    if (tableData?.content.length < 1) return;

    const ipIdsByCountryCode = getValueByCountryCode(country);

    if (!e?.target?.checked) {
      const updatedSelectedRows = selectedRows.filter((id) => !ipIdsByCountryCode.includes(id));
      setSelectedRows(updatedSelectedRows);

      return;
    }

    setSelectedRows([...selectedRows, ...ipIdsByCountryCode]);
  };

  // select all checkbox
  const selectAllCheckbox = async (e, country = null) => {
    if (tableData?.content.length < 1) return;

    if (!e?.target?.checked) {
      setSelectedRows([]);
      return;
    }

    setTableRowLoading(true);

    try {
      const preparedFilterParams = {
        ...filterParams,
        ...(country ? { country } : {})
      };

      clearObjectEmptyValues(preparedFilterParams);

      const { data, status } = await ApiService
        .getAllUserOrdersByFolder(activeData?.proxy?.code, selectedFolder.value, preparedFilterParams, filterByDateParams);

      if (status !== 200) {
        throw Error();
      }

      setSelectedRows(data);
    } catch (e) {
      errorToast("Something went wrong", lang);
    } finally {
      setTableRowLoading(false);
    }
  };

  // first fetching table data
  useEffect(() => {
    if (activeData?.proxy?.code) {
      setLocalSize(10);
      setLocalCurrentPage(1);
      setSelectedRows([]);
      fetchTableData({
        page: 0,
        size: 10,
        proxyType: activeData?.proxy?.code,
        selectedFolder,
        filterParams,
        sort,
        controller,
        filterByDateParams
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeData?.proxy?.code, selectedFolder, searchParams]);

  const rowIsChecked = (row) => selectedRows.some((id) => id === row.id);

  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <IconSvg tag="triangleDown12" />
    </components.DropdownIndicator>
  );

  const getRuText = (daysLeft) => {
    if (daysLeft === 0) return "Заканчивается";
    if (daysLeft === 1) return "Остался 1 день";
    if (daysLeft >= 2 && daysLeft <= 4) return `Осталось ${daysLeft} дня`;

    return `Осталось ${daysLeft} дней`;
  };

  const getEnText = (daysLeft) => {
    if (daysLeft === 0) return "Ending";

    return daysLeft === 1 ? "Left 1 day" : `Left ${daysLeft} days`;
  };

  const getDeadlineText = ({ deadLine }) => {
    const daysLeft = getDaysToDeadline(deadLine);

    if (daysLeft < 0) return;

    const text = lang === "ru"
      ? getRuText(daysLeft)
      : getEnText(daysLeft);

    return <span className="dates-label">{text}</span>;
  };

  const getDateRange = ({ dateStart, deadLine }) => {
    const dateStartFormatted = formatDate(dateStart);
    const deadLineFormatted = formatDate(deadLine);

    return `${dateStartFormatted} - ${deadLineFormatted}`;
  };

  const getIpsByCountryCode = (countryCode) => {
    if (!tableData?.content) return [];

    return tableData?.content.filter((ip) => ip.country === countryCode);
  };

  const isAllIpsByCountrySelected = (ipIds) => {
    if (!selectedRows || !ipIds) return [];

    const selectedSet = new Set(selectedRows);
    return ipIds.every((id) => selectedSet.has(id));
  };

  const updateNotesHandler = (updatedOrder) => {
    const orderForUpdate = tableData.content.find((order) => order.id === updatedOrder.id);

    if (!orderForUpdate) return;

    const updatedTableData = tableData.content.map((order) => {
      if (order.id === updatedOrder.id) return { ...order, description: updatedOrder.description };

      return order;
    });
    setTableData((current) => ({ ...current, content: updatedTableData }));
  };

  const getTableBody = () => {
    if (!isTableLoaded) {
      return (
        Array(10).fill("_").map((_, idx) => (
          <TableRowLoader key={idx} />
        ))
      );
    }

    if (!tableData?.countryIds?.length) {
      return (
        <tr className="table-body-tr nodata">
          <td className="table-body-noData">
            <NoDataComponent>
              <Button tab="confirm" onClick={() => navigate("?tab=new-order")}>{buyBtn}</Button>
            </NoDataComponent>
          </td>
        </tr>
      );
    }

    const extendProxy = (id) => {
      if (!selectedRows.includes(id)) setSelectedRows([id]);

      scrollToElementById("renewal");
    };

    return tableData.countryIds.map((item) => {
      const [[countryCode, ipIdsByCountry]] = Object.entries(item);

      return (
        <>
          <tr className="table-body-tr">
            <td className="table-body-tr-item id">
              <CheckBox
                checked={isAllIpsByCountrySelected(ipIdsByCountry)}
                onChange={(e) => selectAllIpsByCountry(e, countryCode)}
                partly={!isAllIpsByCountrySelected(ipIdsByCountry) && selectedRows.some((id) => ipIdsByCountry.includes(id))}
                disabled={isTableRowLoading}
              />
            </td>
            <td className="table-body-tr-item country-group">
              {`${getCountryName(countryCode, lang, countries?.data)} (${ipIdsByCountry?.length} IPs)`}
            </td>
          </tr>
          {getIpsByCountryCode(countryCode)?.map((row) => (
            <Fragment key={row?.id}>
              <tr
                className={`table-body-tr ${
                  rowIsChecked(row) ? "table-body-tr-active" : ""
                }`}
              >
                <td className="table-body-tr-item id">
                  <FormatCellCheckbox row={row}/>
                </td>
                <td className="table-body-tr-item renew-ip--body">
                  {getCountryFlag(row?.countryInfo?.code, null, lang)}
                  {row?.innerIp}
                </td>
                <td className="table-body-tr-item dates">
                  {getDateRange(row)}
                  {getDeadlineText(row)}
                </td>
                <td className="table-body-tr-item notes-content">
                  <FormatCellNotesData order={row} setIsEditModalOpen={setIsEditModalOpen}/>
                </td>
                <td
                  className="table-body-tr-item renew-table-button"
                  onClick={() => extendProxy(row.id)}
                >
                  <div>{`${formBtn} ${selectedRows?.length && selectedRows.includes(row?.id) ? `(${selectedRows.length} IPs)` : ""}`}</div>
                  <IconSvg tag="arrowRight"/>
                </td>
                {isTableRowLoading && (
                  <Loading tableRow absolute roller={{ transform: "scale(0.5)" }}/>
                )}
              </tr>
            </Fragment>
          ))}
        </>
      );
    });
  };

  const isTableDataCount = tableData?.totalElements;

  return (
    <div className="table__container">
      <div className={tableData?.totalElements ? "table-contain" : ""}>
        <div className="table-box">
          <table className="table">
            <thead className="table-head">
            <tr className="table-head-tr">
              <th className="table-head-tr-item id">
                <CheckBox
                  checked={
                    isTableDataCount > 0 && isTableDataCount === selectedRows.length
                  }
                  onChange={selectAllCheckbox}
                  partly={
                    selectedRows.length > 0 && selectedRows.length !== isTableDataCount
                  }
                  disabled={isTableRowLoading}
                />
              </th>
              <th className="table-head-tr-item filter-table">
                <FormatCellFilter type="renewal" />
              </th>
              <th className="table-head-tr-item renew-ip--head">
                {orders?.column?.ip?.name}
              </th>
              <th className="table-head-tr-item dates">{proxyExpire}</th>
              <th className="table-head-tr-item notes">
                <FormatCellNotesHead title={orders?.column?.notes?.title} />
              </th>
              <th className="table-head-tr-item renew-table-button"></th>
            </tr>
            </thead>
            <tbody className="table-body">
            {getTableBody()}
            </tbody>
          </table>
        </div>
        <div className="table__pagination table__pagination--renewal">
          {isPaginationVisible && (
            <Pagination
              totalPages={tableData?.totalPages}
              currentPage={localCurrentPage}
              onPageChange={handlePageChange}
              hideOnSinglePage={false}
            />
          )}
          {isPaginationVisible && (
            <Select
              className={"modal__select"}
              ref={ref}
              classNamePrefix="modal__select"
              options={defineRowsPerPageOptions()}
              defaultValue={{
                label: `${localSize} ${balance?.lines}`,
                value: localSize
              }}
              menuPlacement="bottom"
              isSearchable={false}
              // placeholder={placeholder}
              onChange={handlePerRowsChange}
              noOptionsMessage={() => noOptionsMessage}
              components={{ DropdownIndicator }}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: getBorderColor(state, skin)
                })
              }}
            />
          )}
        </div>
      </div>
      <UpdateTableDataModal />
      <CustomModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        styles={{ zIndex: "9999999" }}
      >
        <EditNotesModal setIsEditModalOpen={setIsEditModalOpen} updateNotesHandler={updateNotesHandler} />
      </CustomModal>
    </div>
  );
};
